import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import authService from '../services/auth';
import './Setup2FA.css';

const Setup2FA = () => {
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const { enable2FA, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // If user already has 2FA enabled, redirect to dashboard
    if (user && user.is2FAEnabled) {
      navigate('/dashboard');
      return;
    }

    // Generate 2FA setup data
    const setup2FA = async () => {
      try {
        const response = await authService.setup2FA();
        setQrCode(response.qrCode);
        setSecret(response.secret);
        setError('');
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to initialize 2FA setup');
        console.error('2FA setup error:', err);
      } finally {
        setLoading(false);
      }
    };

    setup2FA();
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setVerifying(true);

    try {
      // Verify the token and enable 2FA
      await enable2FA(token, secret);
      // Redirect to dashboard after successful setup
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid verification code. Please try again.');
      console.error('2FA enable error:', err);
    } finally {
      setVerifying(false);
    }
  };

  const handleCopySecret = () => {
    navigator.clipboard.writeText(secret);
    // You could add a toast notification here
    alert('Secret copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="setup-2fa-container">
        <div className="setup-2fa-card">
          <div className="loading">Loading 2FA setup...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="setup-2fa-container">
      <div className="setup-2fa-card">
        <h2>Two-Factor Authentication Setup</h2>
        <p className="setup-description">
          Two-factor authentication is required for all users. Please complete the setup below.
        </p>

        {error && <div className="error-message">{error}</div>}

        <div className="setup-steps">
          <div className="setup-step">
            <h3>Step 1: Install an Authenticator App</h3>
            <p>
              Download an authenticator app on your mobile device such as:
            </p>
            <ul>
              <li>Google Authenticator</li>
              <li>Microsoft Authenticator</li>
              <li>Authy</li>
              <li>1Password</li>
            </ul>
          </div>

          <div className="setup-step">
            <h3>Step 2: Scan the QR Code</h3>
            {qrCode ? (
              <div className="qr-code-container">
                <img src={qrCode} alt="2FA QR Code" className="qr-code" />
                <p className="qr-help">
                  Scan this QR code with your authenticator app
                </p>
              </div>
            ) : (
              <div className="error-message">Failed to generate QR code</div>
            )}
          </div>

          <div className="setup-step">
            <h3>Alternative: Manual Entry</h3>
            <p>If you can't scan the QR code, enter this secret key manually:</p>
            <div className="secret-container">
              <code className="secret-code">{secret}</code>
              <button 
                type="button" 
                onClick={handleCopySecret}
                className="btn-copy"
              >
                Copy
              </button>
            </div>
          </div>

          <div className="setup-step">
            <h3>Step 3: Enter Verification Code</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="token">
                  Enter the 6-digit code from your authenticator app:
                </label>
                <input
                  type="text"
                  id="token"
                  value={token}
                  onChange={(e) => setToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  placeholder="000000"
                  maxLength="6"
                  pattern="[0-9]{6}"
                  className="token-input"
                />
              </div>

              <button 
                type="submit" 
                disabled={verifying || token.length !== 6} 
                className="btn-primary"
              >
                {verifying ? 'Verifying...' : 'Complete Setup'}
              </button>
            </form>
          </div>
        </div>

        <div className="security-notice">
          <strong>Security Notice:</strong> Two-factor authentication adds an extra layer of 
          security to your account. Keep your authenticator app secure and backed up.
        </div>
      </div>
    </div>
  );
};

export default Setup2FA;
